import React, { useEffect, useMemo, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import authService from '../../services/authService';
import '../admin/Admin.css';
import '../auth/Auth.css';

const SubInner = ({ publishableKey, teamPriceId, enterprisePriceId }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [plan, setPlan] = useState('team');
  const [status, setStatus] = useState('');
  const [subStatus, setSubStatus] = useState('');
  const [pending, setPending] = useState(false);

  const priceId = plan === 'enterprise' ? enterprisePriceId : teamPriceId;

  const fetchStatus = async () => {
    const t = authService.getToken && authService.getToken();
    try {
      const r = await fetch('/api/payments/subscription/status', { headers: t?{ Authorization:`Bearer ${t}` }: {}, credentials:'include' });
      if (r.ok) { const j = await r.json(); setSubStatus(j?.status || 'None'); }
    } catch {}
  };

  useEffect(() => { fetchStatus(); }, []);

  const handleCreateOrUpdate = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setStatus('');
    setPending(true);
    const t = authService.getToken && authService.getToken();
    try {
      if (subStatus && subStatus !== 'None') {
        const r = await fetch('/api/payments/subscription/update', { method:'POST', headers: { 'Content-Type':'application/json', ...(t?{ Authorization:`Bearer ${t}` }: {}) }, credentials:'include', body: JSON.stringify({ PriceId: priceId }) });
        if (!r.ok) throw new Error('Failed to update');
        setStatus('Subscription updated.');
        await fetchStatus();
      } else {
        const r = await fetch('/api/payments/subscription', { method:'POST', headers: { 'Content-Type':'application/json', ...(t?{ Authorization:`Bearer ${t}` }: {}) }, credentials:'include', body: JSON.stringify({ PriceId: priceId }) });
        if (!r.ok) throw new Error('Failed to create');
        const j = await r.json();
        const clientSecret = j?.clientSecret;
        if (clientSecret) {
          const card = elements.getElement(CardElement);
          const result = await stripe.confirmCardPayment(clientSecret, { payment_method: { card }, return_url: window.location.origin + '/subscriptions' });
          if (result.error) setStatus(result.error.message || 'Payment failed');
          else {
            setStatus('Subscription started. Applying credits…');
            try {
              // Latest invoice's PI may have been returned when creating the sub; reload status and refresh balance either way
              localStorage.setItem('balanceRefreshTick', String(Date.now()));
              setTimeout(()=>{ try { localStorage.setItem('balanceRefreshTick', String(Date.now())); } catch {} }, 1500);
            } catch {}
          }
        } else setStatus('Subscription created.');
        await fetchStatus();
      }
    } catch (e) {
      setStatus(e.message || 'Action failed');
    } finally { setPending(false); }
  };

  const handleCancel = async () => {
    const t = authService.getToken && authService.getToken();
    try {
      const r = await fetch('/api/payments/subscription/cancel', { method:'POST', headers: t?{ Authorization:`Bearer ${t}` }: {}, credentials:'include' });
      if (!r.ok) throw new Error('Failed to cancel');
      setStatus('Subscription canceled.');
      try { localStorage.setItem('balanceRefreshTick', String(Date.now())); } catch {}
      await fetchStatus();
    } catch (e) { setStatus(e.message || 'Cancel failed'); }
  };

  return (
    <div className="admin-container">
      <div className="admin-section">
        <h2 className="admin-header">Subscription Management</h2>
        <div className="dashboard-card" style={{ maxWidth: 640, margin:'0 auto' }}>
          <div style={{ marginBottom: 10 }}>
            <label><input type="radio" name="plan" value="team" checked={plan==='team'} onChange={()=>setPlan('team')} /> <span style={{ marginLeft:8 }}>Team</span></label>
            <label style={{ marginLeft: 16 }}><input type="radio" name="plan" value="enterprise" checked={plan==='enterprise'} onChange={()=>setPlan('enterprise')} /> <span style={{ marginLeft:8 }}>Enterprise</span></label>
          </div>
          <div style={{ marginBottom: 10, color:'#64748b' }}>Current status: <strong>{subStatus || 'None'}</strong></div>
          <form onSubmit={handleCreateOrUpdate}>
            <div style={{ border:'1px solid #e5e7eb', padding: 12, borderRadius: 8, background:'#fff' }}>
              <CardElement options={{ style: { base: { fontSize:'16px' } } }} />
            </div>
            <div style={{ display:'flex', gap:8, marginTop: 12, justifyContent:'space-between' }}>
              <button type="submit" className="action-button primary" disabled={!stripe || pending}>{subStatus && subStatus!=='None' ? (pending ? 'Updating…' : 'Update Plan') : (pending ? 'Starting…' : 'Start Subscription')}</button>
              {subStatus && subStatus!=='None' && (<button type="button" className="action-button secondary" onClick={handleCancel} disabled={pending}>Cancel</button>)}
            </div>
          </form>
          {status && (<div style={{ marginTop: 10, color: status.toLowerCase().includes('fail') ? '#b91c1c' : '#065f46' }}>{status}</div>)}
        </div>
      </div>
    </div>
  );
};

const SubscriptionManager = () => {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { (async ()=>{ try { const r = await fetch('/api/payments/config'); if (r.ok) setCfg(await r.json()); } catch {} })(); }, []);
  const stripePromise = useMemo(() => cfg?.publishableKey ? loadStripe(cfg.publishableKey) : null, [cfg]);
  if (!cfg) return (<div className="admin-container"><div className="admin-section"><div className="dashboard-card">Loading…</div></div></div>);
  return (
    <Elements stripe={stripePromise}>
      <SubInner publishableKey={cfg.publishableKey} teamPriceId={cfg.teamPriceId} enterprisePriceId={cfg.enterprisePriceId} />
    </Elements>
  );
};

export default SubscriptionManager;


